import React from "react";
import {
  ACTIVATIONS,
  BENEFIT_KINDS,
  CHARGE_MODELS,
  DISPLAY_LIMITS,
  UNLOCK_TYPES,
  emptyBenefit,
  removeBenefit,
  renameBenefitKey,
} from "./draftModel";
import { CheckField, Messages, NumberField, SelectField, Section, TextField } from "./fields";

const BenefitsSection = ({ form, setForm, msgs, locked }) => {
  const patch = (index, changes) =>
    setForm((prev) => ({
      ...prev,
      benefits: prev.benefits.map((benefit, i) => (i === index ? { ...benefit, ...changes } : benefit)),
    }));

  return (
    <Section
      id="benefits"
      title="Benefits"
      description="What the rider gets with this plan. A benefit paid upfront needs a rate under Add-ons and coupon."
      messages={msgs("benefits")}
    >
      {form.benefits.map((benefit, index) => {
        const at = (field) => `benefits.${index}.${field}`;
        const id = (field) => `warranty-benefit-${index}-${field}`;
        return (
          <div className="warranty-subcard" key={index}>
            <div className="warranty-subcard-head">
              <strong>{benefit.title || benefit.label || `Benefit ${index + 1}`}</strong>
              <button
                type="button"
                className="warranty-inline-btn is-danger"
                disabled={locked}
                aria-label={`Remove benefit ${benefit.title || benefit.label || index + 1}`}
                onClick={() => setForm((prev) => removeBenefit(prev, index))}
              >
                Remove
              </button>
            </div>
            <Messages messages={msgs(`benefits.${index}`)} />
            <div className="row">
              <div className="col-md-4">
                <TextField
                  id={id("key")}
                  label="Key"
                  value={benefit.key}
                  maxLength={32}
                  disabled={locked}
                  messages={msgs(at("key"))}
                  onChange={(value) => setForm((prev) => renameBenefitKey(prev, index, value))}
                />
              </div>
              <div className="col-md-8">
                <TextField
                  id={id("label")}
                  label="Label"
                  value={benefit.label}
                  maxLength={80}
                  disabled={locked}
                  messages={msgs(at("label"))}
                  onChange={(value) => patch(index, { label: value })}
                />
              </div>
              <div className="col-md-4">
                <TextField
                  id={id("title")}
                  label="Title"
                  value={benefit.title}
                  maxLength={DISPLAY_LIMITS.benefitTitle}
                  disabled={locked}
                  placeholder="for example +2 extra years"
                  help="Shown in the app; may use {tierName} and {couponValue}. With the label blank, the service copies the title into it."
                  messages={msgs(at("title"))}
                  onChange={(value) => patch(index, { title: value })}
                />
              </div>
              <div className="col-md-8">
                <TextField
                  id={id("subtitle")}
                  label="Subtitle"
                  value={benefit.subtitle}
                  maxLength={DISPLAY_LIMITS.benefitSubtitle}
                  disabled={locked}
                  placeholder="for example Of motor warranty"
                  help="Optional. Shown under the title in the app."
                  messages={msgs(at("subtitle"))}
                  onChange={(value) => patch(index, { subtitle: value })}
                />
              </div>
              {benefit.kind === "free_service" && (
                <div className="col-md-4">
                  <NumberField
                    id={id("worth")}
                    label="Worth (₹)"
                    kind="rupees"
                    value={benefit.worth}
                    disabled={locked}
                    help="Optional, per service. The app shows the free services as one card, Worth the sum; blank says Complimentary."
                    messages={msgs(at("worthMinor"))}
                    onChange={(value) => patch(index, { worth: value })}
                  />
                </div>
              )}
              <div className="col-md-4">
                <SelectField
                  id={id("kind")}
                  label="Kind"
                  value={benefit.kind}
                  options={BENEFIT_KINDS}
                  disabled={locked}
                  messages={msgs(at("kind"))}
                  onChange={(value) => patch(index, { kind: value })}
                />
              </div>
              <div className="col-md-4">
                <SelectField
                  id={id("chargeModel")}
                  label="How it is charged"
                  value={benefit.chargeModel}
                  options={CHARGE_MODELS}
                  disabled={locked}
                  messages={msgs(at("chargeModel"))}
                  onChange={(value) => patch(index, { chargeModel: value })}
                />
              </div>
              <div className="col-md-4">
                <SelectField
                  id={id("activation")}
                  label="Activation"
                  value={benefit.activation}
                  options={ACTIVATIONS}
                  disabled={locked}
                  messages={msgs(at("activation"))}
                  onChange={(value) => patch(index, { activation: value })}
                />
              </div>
              <div className="col-md-4">
                <NumberField
                  id={id("termMonths")}
                  label="Term"
                  kind="whole"
                  unit="months"
                  value={benefit.termMonths}
                  disabled={locked}
                  messages={msgs(at("termMonths"))}
                  onChange={(value) => patch(index, { termMonths: value })}
                />
              </div>
              <div className="col-md-4">
                <SelectField
                  id={id("unlockType")}
                  label="Unlocks"
                  value={benefit.unlockType}
                  options={UNLOCK_TYPES}
                  disabled={locked}
                  messages={msgs(at("unlock.type"))}
                  onChange={(value) => patch(index, { unlockType: value })}
                />
              </div>
              {benefit.unlockType === "paid_months" && (
                <div className="col-md-4">
                  <NumberField
                    id={id("unlockMonths")}
                    label="Paid months needed"
                    kind="whole"
                    unit="months"
                    value={benefit.unlockMonths}
                    disabled={locked}
                    messages={msgs(at("unlock.months"))}
                    onChange={(value) => patch(index, { unlockMonths: value })}
                  />
                </div>
              )}
              {benefit.unlockType === "months_after_start" && (
                <div className="col-md-4">
                  <NumberField
                    id={id("unlockMonths")}
                    label="Months after purchase"
                    kind="whole"
                    unit="months"
                    value={benefit.unlockMonths}
                    disabled={locked}
                    messages={msgs(at("unlock.months"))}
                    onChange={(value) => patch(index, { unlockMonths: value })}
                  />
                </div>
              )}
              {benefit.unlockType === "date" && (
                <div className="col-md-4">
                  <TextField
                    id={id("unlockDate")}
                    label="Unlock date"
                    type="date"
                    value={benefit.unlockDate}
                    disabled={locked}
                    messages={msgs(at("unlock.on"))}
                    onChange={(value) => patch(index, { unlockDate: value })}
                  />
                </div>
              )}
              <div className="col-md-12 warranty-checks">
                <CheckField
                  id={id("followsPlanState")}
                  label="Follows the plan state (pauses and stops with the plan)"
                  checked={benefit.followsPlanState}
                  disabled={locked}
                  onChange={(value) => patch(index, { followsPlanState: value })}
                />
                {benefit.kind === "roadside" && (
                  <CheckField
                    id={id("bundledWith")}
                    label="Charged with the theft line (Theft + RSA, one price)"
                    checked={benefit.chargeModel === "included" && (benefit.bundledWith || "") !== ""}
                    disabled={locked}
                    onChange={(value) => {
                      const theft = form.benefits.find((other) => other.kind === "theft" && other.key !== "");
                      patch(
                        index,
                        value && theft
                          ? { chargeModel: "included", bundledWith: theft.key }
                          : { chargeModel: "upfront", bundledWith: "" },
                      );
                    }}
                  />
                )}
                {benefit.unlockType === "paid_months" && (
                  <CheckField
                    id={id("unlockImmediate")}
                    label="Unlocks at once when paid in one go"
                    checked={benefit.unlockImmediate}
                    disabled={locked}
                    onChange={(value) => patch(index, { unlockImmediate: value })}
                  />
                )}
              </div>
            </div>
          </div>
        );
      })}
      <button
        type="button"
        className="warranty-inline-btn is-primary"
        disabled={locked}
        onClick={() => setForm((prev) => ({ ...prev, benefits: [...prev.benefits, emptyBenefit()] }))}
      >
        Add benefit
      </button>
    </Section>
  );
};

export default BenefitsSection;
